import {
  ActionReducerMapBuilder,
  createAsyncThunk,
  EntityState,
} from '@reduxjs/toolkit';
import { classToPlain } from 'class-transformer';

import { AppError } from '@vk-hr-tek/core/error';

import {
  CompanyEventsStat,
  CompanyGroupUsers,
  GetCompanyEventsStatParams,
  SettingsService,
} from '@app/gen/settings';
import { Entity } from '@app/types';

import { ThunkExtra } from '../../../../app/store';
import {
  EventStat,
  SettingsState,
  SettingsWithRootState,
} from '../../settings.state';
import { SettingsDetailMapper } from '../../../mappers';

export const getEventStat = createAsyncThunk<
  EventStat[],
  GetCompanyEventsStatParams,
  ThunkExtra<SettingsWithRootState>
>(
  'settings/getEventStat',
  async ({ company_id }, { rejectWithValue, extra: { inject } }) => {
    try {
      const settingsService = await inject(SettingsService);
      const result = await settingsService.getCompanyEventsStat({
        params: { company_id },
      });

      const mapper = await inject(SettingsDetailMapper);

      return mapper.processEventStat(result.events_stat);
    } catch (err) {
      return rejectWithValue(classToPlain(err) as AppError);
    }
  },
);

export const settingsStatReducers = (
  builder: ActionReducerMapBuilder<
    EntityState<Entity<CompanyEventsStat | CompanyGroupUsers>, string> &
      SettingsState
  >,
) => {
  builder.addCase(getEventStat.pending, (state, { meta }) => {
    state.eventsStat[meta.arg.company_id] = {
      status: 'loading',
      error: null,
      stat: [],
    };
  });
  builder.addCase(getEventStat.fulfilled, (state, { payload, meta }) => {
    state.eventsStat[meta.arg.company_id] = {
      status: 'complete',
      error: null,
      stat: payload,
    };
  });
  builder.addCase(getEventStat.rejected, (state, { payload, error, meta }) => {
    state.eventsStat[meta.arg.company_id] = {
      status: 'failed',
      error:
        payload ||
        ({
          info: (error && error.message) || 'Unknown error',
          status: 500,
          source: 'client',
          title: 'Internal client error',
        } as AppError),
      stat: [],
    };
  });
};
